// Well-known business systems and the website their logo comes from. Used to fill in a system's website on the Access
// inventory form and to show a logo when none was entered. Matching is on whole words, with a small typo allowance for
// longer names ("Salseforce" -> Salesforce), so short names like "box" or "sap" only match as a word on their own.
const SYSTEMS: { domain: string; names: string[] }[] = [
  { domain: 'salesforce.com', names: ['salesforce', 'sfdc'] },
  { domain: 'aws.amazon.com', names: ['aws', 'amazon web services', 's3', 'ec2'] },
  { domain: 'azure.microsoft.com', names: ['azure', 'entra'] },
  { domain: 'cloud.google.com', names: ['gcp', 'google cloud', 'bigquery'] },
  { domain: 'workspace.google.com', names: ['google workspace', 'g suite', 'gsuite', 'gmail', 'google drive'] },
  { domain: 'microsoft.com', names: ['microsoft 365', 'office 365', 'o365', 'm365', 'sharepoint', 'outlook', 'onedrive', 'microsoft teams', 'dynamics'] },
  { domain: 'okta.com', names: ['okta'] },
  { domain: 'slack.com', names: ['slack'] },
  { domain: 'github.com', names: ['github'] },
  { domain: 'gitlab.com', names: ['gitlab'] },
  { domain: 'bitbucket.org', names: ['bitbucket'] },
  { domain: 'atlassian.com', names: ['atlassian', 'jira', 'confluence'] },
  { domain: 'zoom.us', names: ['zoom'] },
  { domain: 'workday.com', names: ['workday'] },
  { domain: 'servicenow.com', names: ['servicenow'] },
  { domain: 'snowflake.com', names: ['snowflake'] },
  { domain: 'datadoghq.com', names: ['datadog'] },
  { domain: 'stripe.com', names: ['stripe'] },
  { domain: 'hubspot.com', names: ['hubspot'] },
  { domain: 'zendesk.com', names: ['zendesk'] },
  { domain: 'dropbox.com', names: ['dropbox'] },
  { domain: 'box.com', names: ['box'] },
  { domain: 'oracle.com', names: ['oracle'] },
  { domain: 'netsuite.com', names: ['netsuite'] },
  { domain: 'sap.com', names: ['sap', 'successfactors', 'concur'] },
  { domain: 'cloudflare.com', names: ['cloudflare'] },
  { domain: 'mongodb.com', names: ['mongodb', 'mongo atlas'] },
  { domain: 'notion.so', names: ['notion'] },
  { domain: 'asana.com', names: ['asana'] },
  { domain: 'monday.com', names: ['monday'] },
  { domain: 'trello.com', names: ['trello'] },
  { domain: 'figma.com', names: ['figma'] },
  { domain: 'docusign.com', names: ['docusign'] },
  { domain: 'adobe.com', names: ['adobe'] },
  { domain: 'shopify.com', names: ['shopify'] },
  { domain: 'quickbooks.intuit.com', names: ['quickbooks', 'intuit'] },
  { domain: 'xero.com', names: ['xero'] },
  { domain: 'paypal.com', names: ['paypal'] },
  { domain: 'twilio.com', names: ['twilio'] },
  { domain: 'sendgrid.com', names: ['sendgrid'] },
  { domain: 'mailchimp.com', names: ['mailchimp'] },
  { domain: 'intercom.com', names: ['intercom'] },
  { domain: 'freshworks.com', names: ['freshdesk', 'freshservice', 'freshworks'] },
  { domain: 'jamf.com', names: ['jamf'] },
  { domain: 'crowdstrike.com', names: ['crowdstrike', 'falcon'] },
  { domain: 'sentinelone.com', names: ['sentinelone'] },
  { domain: '1password.com', names: ['1password'] },
  { domain: 'lastpass.com', names: ['lastpass'] },
  { domain: 'auth0.com', names: ['auth0'] },
  { domain: 'onelogin.com', names: ['onelogin'] },
  { domain: 'duo.com', names: ['duo'] },
  { domain: 'fortinet.com', names: ['fortinet', 'fortigate', 'forticlient'] },
  { domain: 'paloaltonetworks.com', names: ['palo alto', 'globalprotect', 'prisma'] },
  { domain: 'cisco.com', names: ['cisco', 'anyconnect', 'webex', 'meraki'] },
  { domain: 'vercel.com', names: ['vercel'] },
  { domain: 'netlify.com', names: ['netlify'] },
  { domain: 'heroku.com', names: ['heroku'] },
  { domain: 'digitalocean.com', names: ['digitalocean'] },
  { domain: 'docker.com', names: ['docker'] },
  { domain: 'jenkins.io', names: ['jenkins'] },
  { domain: 'pagerduty.com', names: ['pagerduty'] },
  { domain: 'splunk.com', names: ['splunk'] },
  { domain: 'elastic.co', names: ['elastic', 'elasticsearch', 'kibana'] },
  { domain: 'newrelic.com', names: ['new relic', 'newrelic'] },
  { domain: 'sentry.io', names: ['sentry'] },
  { domain: 'airtable.com', names: ['airtable'] },
  { domain: 'tableau.com', names: ['tableau'] },
  { domain: 'powerbi.microsoft.com', names: ['power bi', 'powerbi'] },
  { domain: 'looker.com', names: ['looker'] },
  { domain: 'bamboohr.com', names: ['bamboohr'] },
  { domain: 'gusto.com', names: ['gusto'] },
  { domain: 'rippling.com', names: ['rippling'] },
  { domain: 'adp.com', names: ['adp'] },
  { domain: 'greenhouse.io', names: ['greenhouse'] },
  { domain: 'linear.app', names: ['linear'] },
  { domain: 'clickup.com', names: ['clickup'] },
  { domain: 'miro.com', names: ['miro'] },
  { domain: 'canva.com', names: ['canva'] },
  { domain: 'zapier.com', names: ['zapier'] },
];

const words = (text: string) => text.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();

function distance(a: string, b: string): number {
  if (Math.abs(a.length - b.length) > 2) return 3;
  let prev = Array.from({ length: b.length + 1 }, (_, i) => i);
  for (let i = 1; i <= a.length; i++) {
    const row = [i];
    for (let j = 1; j <= b.length; j++) {
      row[j] = Math.min(prev[j] + 1, row[j - 1] + 1, prev[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
    }
    prev = row;
  }
  return prev[b.length];
}

/** The website of a well-known system named in `systemName`, or null. */
export function guessSystemDomain(systemName: string): string | null {
  const text = ` ${words(systemName)} `;
  if (!text.trim()) return null;
  for (const system of SYSTEMS) {
    if (system.names.some(name => text.includes(` ${name} `))) return system.domain;
  }
  // Typo allowance, only for single-word names of 6+ letters: one edit for 6-7 letters, two for longer.
  const tokens = text.trim().split(' ');
  for (const system of SYSTEMS) {
    for (const name of system.names) {
      if (name.includes(' ') || name.length < 6) continue;
      const allowed = name.length >= 8 ? 2 : 1;
      if (tokens.some(token => token.length >= 5 && distance(token, name) <= allowed)) return system.domain;
    }
  }
  return null;
}
